import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { cardBackground, cardBorder } from '../styles/themeHelpers';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PasswordField from '../components/common/PasswordField';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import { formatPhoneNumber } from '../utils/validation';

const Background = styled.div`
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors.background};
  position: relative;
  
  /* Desktop - fixed height, no scroll */
  @media (min-width: 769px) {
    height: 100vh;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  }
  
  /* Mobile - scrollable */
  @media (max-width: 768px) {
    min-height: 100vh;
    overflow: visible;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${({ theme }) =>
      theme.profile?.pageBackground === 'transparent'
        ? 'none'
        : "url('data:image/svg+xml,%3Csvg width=\\'2\\' height=\\'2\\' xmlns=\\'http://www.w3.org/2000/svg\\'%3E%3Ccircle cx=\\'1\\' cy=\\'1\\' r=\\'0.2\\' fill=\\'#003040\\' fill-opacity=\\'0.3\\'/%3E%3C/svg%3E')"};
    pointer-events: none;
  }
`;


const CenterBlock = styled.div`
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Desktop - centered, fixed position */
  @media (min-width: 769px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    justify-content: center;
    padding: 15px;
    overflow: hidden;
  }
  
  /* Mobile - scrollable from top */
  @media (max-width: 768px) {
    position: relative;
    justify-content: flex-start;
    min-height: 100vh;
    padding: 80px 15px 20px; /* Top padding to avoid header */
  }
`;

const AccessFrame = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 2px solid ${({ theme }) => cardBorder(theme)};
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
  padding: 25px;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  position: relative;
  text-align: center;
  z-index: 10;
  backdrop-filter: none;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    max-width: 420px;
    padding: 20px;
  }
`;

const AccessTitle = styled.h1`
  font-size: 1.5rem;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
`;

const SubStatus = styled.div`
  color: #b0b0b0;
  font-size: 13px;
  margin-bottom: 18px;
  letter-spacing: 1px;
`;

const InputBlock = styled.div`
  margin-bottom: 8px;
`;

const InputLabel = styled.div`
  color: #00d4ff;
  font-size: 13px;
  margin-bottom: 5px;
  letter-spacing: 1px;
  text-align: left;
  font-weight: 500;
`;

const InputField = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  padding: 8px 12px;
  margin-bottom: 0;
  outline: none;
  text-align: left;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
`;

const StatusText = styled.div`
  color: #b0b0b0;
  font-size: 10px;
  margin-bottom: 6px;
  letter-spacing: 1px;
  text-align: left;
`;

const EncryptionText = styled.div`
  color: #b0b0b0;
  font-size: 10px;
  margin-bottom: 6px;
  letter-spacing: 1px;
  text-align: left;
`;

const RegisterButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 10px 0;
  margin: 15px 0;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
`;

const FrameLink = styled(Link)`
  color: #00d4ff;
  font-size: 12px;
  text-decoration: none;
  letter-spacing: 1px;
  padding: 6px 12px;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    color: #fff;
    border-color: #00d4ff;
    background: rgba(0, 212, 255, 0.1);
  }
`;



const InfoOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'rgba(0, 0, 0, 0.65)'
      : theme.gradients?.background || theme.colors.background};
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: none;
`;

const InfoBox = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 2px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 30px;
  max-width: 400px;
  width: 90%;
  position: relative;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
  backdrop-filter: none;
`;

const InfoTitle = styled.h3`
  color: #00d4ff;
  font-size: 1.4rem;
  margin-bottom: 20px;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
`;

const InfoText = styled.p`
  color: #e0e0e0;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 15px;
  text-align: center;
`;

const InfoButton = styled.button`
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: bold;
  text-transform: uppercase;
  padding: 12px 24px;
  margin: 20px auto;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  display: block;
  
  &:hover {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
  }
`;

const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [formattedPhone, setFormattedPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();
  const { notifications, removeNotification, showError } = useNotification();

  // Обработчик изменения телефона
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Запрещаем редактирование "+1 " в начале, если оно есть
    if (inputValue.startsWith('+1') && !phone.startsWith('+1')) {
      setPhone('+1 ');
      setFormattedPhone('+1 ');
      return;
    }
    
    // Добавляем проверку на допустимые символы: цифры, пробелы, (), -, +
    const isValidChar = /^[0-9\s()\-+]*$/.test(inputValue);
    
    if (isValidChar) {
      setPhone(inputValue);
      setFormattedPhone(formatPhoneNumber(inputValue));
    }
  };

  // Форматируем телефон при вводе
  useEffect(() => {
    setFormattedPhone(formatPhoneNumber(phone));
  }, [phone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Валидация имени (минимум 3 символа)
    if (name.trim().length < 3) {
      showError('Invalid Callsign', 'COMANDOS CALLSIGN MUST BE AT LEAST 3 CHARACTERS');
      return;
    }

    if (!passwordValid) {
      showError('Invalid Password', 'Password does not meet requirements or does not match');
      return;
    }

    setLoading(true);

    try {
      const cleanPhone = phone.replace(/\D/g, '');
      await register(name, email, password, cleanPhone);
      // После регистрации показываем сообщение о необходимости подтверждения email
      setRegistrationComplete(true);
    } catch (err: any) {
      console.error('Registration error:', err);
      
      // Получаем точный текст ошибки от API
      const errorMessage = err.response?.data?.message || err.message || 'REGISTRATION FAILED: ACCESS DENIED';
      
      // Если это ошибка о существующем пользователе, используем более понятный заголовок
      if (errorMessage.includes('User with this email already exists')) {
        showError('User Already Exists', errorMessage);
      } else {
        showError('Registration Failed', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Модальное окно после успешной регистрации
  if (registrationComplete) {
    return (
      <InfoOverlay>
        <InfoBox>
          <InfoTitle>Registration Successful</InfoTitle>
          
          <InfoText>
            Please check your email ({email}) to verify your account.
          </InfoText>
          
          <InfoText>
            A verification link has been sent to your email. Click the link to complete your registration.
          </InfoText>
          
          <InfoText>
            <strong style={{ color: '#00d4ff' }}>IMPORTANT:</strong> The verification link will expire in 24 hours.
          </InfoText>
          
          <InfoButton onClick={() => navigate('/')}>
            Return to Home
          </InfoButton>
        </InfoBox>
      </InfoOverlay>
    );
  }

  return (
    <Background>
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      
      <CenterBlock>
        <AccessFrame>
          <AccessTitle>Create Account</AccessTitle>
          <SubStatus>Join the NovaWarfare community</SubStatus>
          
          <form onSubmit={handleSubmit}>
            
            <InputBlock>
              <InputLabel>Name</InputLabel>
              <InputField
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                placeholder="Enter your name"
              />
              <StatusText>Minimum 3 characters</StatusText>
            </InputBlock>

            <InputBlock>
              <InputLabel>Email Address</InputLabel>
              <InputField
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                placeholder="Enter your email"
              />
              <StatusText>Format: user@domain.com</StatusText>
            </InputBlock>

            <InputBlock>
              <InputLabel>Phone Number</InputLabel>
              <InputField
                type="tel"
                value={formattedPhone}
                onChange={handlePhoneChange}
                required
                inputMode="tel"
                placeholder="(555) 123-4567"
                maxLength={17}
              />
              <StatusText>Format: (555) 123-4567</StatusText>
            </InputBlock>

            <InputBlock>
              <PasswordField
                label="Password"
                value={password}
                onChange={setPassword}
                withConfirm
                confirmValue={confirmPassword}
                onConfirmChange={setConfirmPassword}
                emailForChecks={email}
                autoComplete="new-password"
                onValidityChange={(ok) => setPasswordValid(ok)}
                showInlineChecklist={false}
                showHelp
              />
            </InputBlock>
            
            <RegisterButton type="submit" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </RegisterButton>
          </form>
          
          <LinkRow>
            <FrameLink to="/login">Already have an account?</FrameLink>
            <FrameLink to="/">Return to Home</FrameLink>
          </LinkRow>
        </AccessFrame>
      </CenterBlock>
    </Background>
  );
};

export default RegisterPage; 
